import { Alert, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { ConfirmationNumberOutlined, CreditCard } from '@mui/icons-material';
import { EventTicketsFieldArray } from './eventTicketsFieldArray';
import { EventFeeCollapsible } from './eventFeeCollapsible';

type EventFormStepParticipantsProps = {
  eventId?: number;
  editMode?: boolean;
};

export const EventFormStepParticipants = (props: EventFormStepParticipantsProps) => {
  const { eventId = undefined, editMode = undefined } = props;
  const { control, setValue } = useFormContext();
  const isMounted = useIsMounted();
  const [hasInvoiceGroups, setHasInvoiceGroups] = useState<boolean>(false);

  const ownerId: number | undefined = useWatch({ control, name: 'owner_id' });
  const feeValue: number | undefined = useWatch({ control, name: 'fee' });
  const memberFeeValue: number | undefined = useWatch({ control, name: 'memberFee' });
  const useTickets: boolean = useWatch({ control, name: 'useTickets' }) ?? false;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllInvoiceGroups(0, 500, 'name', 'asc', '');
        if (isMounted()) setHasInvoiceGroups(result.data.results.length > 0);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    };
    fetchData();
  }, [isMounted]);

  const hasOwnerId = !!ownerId;

  const selectFlatFee = useCallback(() => {
    setValue('useTickets', false, { shouldDirty: true, shouldValidate: true });
    // ensure tickets is a valid empty array (or strip it server-side)
    // setValue('tickets', [{ ticketName: '', price: undefined, memberPrice: undefined, isEnabledAt: undefined }]);
    if (feeValue === undefined) setValue('fee', 0);
    if (memberFeeValue === undefined) setValue('memberFee', 0);
  }, [setValue, memberFeeValue, feeValue]);

  const selectTickets = useCallback(() => {
    setValue('useTickets', true, { shouldDirty: true, shouldValidate: true });
    // setValue('tickets', [DEFAULT_TICKET]);
    setValue('fee', undefined);
    setValue('memberFee', undefined);
  }, [setValue]);

  const ownerWarning = useMemo(
    () =>
      !hasOwnerId && hasInvoiceGroups ? (
        <Alert severity='warning' sx={{ border: (thm) => `1px solid ${thm.palette.divider}` }}>
          {t('event:noOwnerIdMessage')}
        </Alert>
      ) : null,
    [hasOwnerId, hasInvoiceGroups],
  );

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              {t('event:participants')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              name='minParticipants'
              type='number'
              required
              label={t('event:formFields.minParticipants')}
              inputProps={{ maxLength: 50 }} // TODO
              InputLabelProps={{
                shrink: true,
              }}
              inputMode='numeric'
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextField
              name='maxParticipants'
              type='number'
              required
              label={t('event:formFields.maxParticipants')}
              inputProps={{ maxLength: 50 }} // TODO
              InputLabelProps={{
                shrink: true,
              }}
              inputMode='numeric'
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid item xs={12}>
          <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
            {t('event:fee')}
          </Typography>
        </Grid>
        <EventFeeCollapsible
          title={t('event:fixedFee')}
          subtitle={t('event:singlePrice')}
          icon={<CreditCard fontSize='small' />}
          selected={!useTickets}
          onSelect={selectFlatFee}
        >
          <Grid container spacing={2}>
            {ownerWarning ? (
              <Grid item xs={12}>
                {ownerWarning}
              </Grid>
            ) : (
              <>
                <Grid item xs={6}>
                  <ControlledTextField
                    name='fee'
                    type='number'
                    required={hasOwnerId}
                    disabled={!hasOwnerId}
                    label={t('event:formFields.fee')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputMode='numeric'
                  />
                </Grid>
                <Grid item xs={6}>
                  <ControlledTextField
                    name='memberFee'
                    type='number'
                    required={hasOwnerId}
                    disabled={!hasOwnerId}
                    label={t('event:formFields.memberFee')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputMode='numeric'
                  />
                </Grid>
              </>
            )}
            {hasOwnerId && (
              <Grid item xs={12}>
                <Alert severity='info' sx={{ border: (thm) => `1px solid ${thm.palette.divider}}` }}>
                  {t('event:priceInformation', { memberFeeLabel: t('event:formFields.memberFee') })}
                </Alert>
              </Grid>
            )}
          </Grid>
        </EventFeeCollapsible>

        {/* BILJETTER */}
        <EventFeeCollapsible
          title={t('event:tickets')}
          subtitle={t('event:multipleTickets')}
          icon={<ConfirmationNumberOutlined fontSize='small' />}
          disabled={!hasOwnerId && !hasInvoiceGroups}
          selected={useTickets}
          onSelect={selectTickets}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {ownerWarning ? (
                    <Grid item xs={12}>
                      {ownerWarning}
                    </Grid>
                  ) : (
                    <EventTicketsFieldArray editMode={editMode} eventId={eventId} />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </EventFeeCollapsible>
      </Grid>
    </GridWrapper>
  );
};
